import React, { useEffect } from 'react';
import { BrandSymbolWithText } from '../../../../../patterns/00-atoms/brand/brand-symbol-with-text.tsx';
import { Icon } from '../../../../../patterns/00-atoms/icon/icon.tsx';
import { useIsMobile } from '../../../../../utils/hooks/useIsMobile.ts';
import { Button, TextIconButton } from '../../../../../patterns/00-atoms/button/button.tsx';
import { UBTracking } from '../../../../../components/tracking/ubtracking.ts';
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetFooter,
  SheetTrigger
} from '../../../../../patterns/00-atoms/cn/sheet.tsx';
import {
  FacebookLogo,
  InstagramLogo
} from '../../../../../patterns/00-atoms/social-media-logos/social-media-logos.tsx';
import { classList } from '../../../../../utils/string.ts';
import { prgLink } from '../../../../../utils/prg-resolver.ts';

/**
 * TODO Check to what extent the ref attribute is relevant for passing.
 */

export type HeaderProps = {
  showSearch: boolean;
  setShowSearch: (showSearch: boolean) => void;
  showSearchIcon?: boolean;
};

export const Header: React.FunctionComponent<HeaderProps> = props => {
  const logoRef = React.useRef<HTMLAnchorElement>(null);
  const loginLinkRef = React.useRef<HTMLAnchorElement>(null);
  const loginButtonRef = React.useRef<HTMLButtonElement | HTMLAnchorElement>(null);
  const searchIconRef = React.useRef<HTMLAnchorElement>(null);
  const { showSearch, setShowSearch } = props;
  const showSearchIcon = props.showSearchIcon ?? true;
  const { isMobile } = useIsMobile();
  const [menuOpen, setMenuOpen] = React.useState(false);
  const toggleSearch = () => setShowSearch(!showSearch);

  useEffect(() => {
    if (logoRef.current) {
      UBTracking.onTrackTargetView(logoRef.current, 'header_bar', 'logo');
    }
    if (isMobile && loginLinkRef.current) {
      UBTracking.onTrackTargetView(loginLinkRef.current, 'header_bar', 'login', 'icon');
    }
    if (!isMobile && loginButtonRef.current) {
      UBTracking.onTrackTargetView(loginButtonRef.current, 'header_bar', 'login', 'button');
    }
    if (searchIconRef.current) {
      UBTracking.onTrackTargetView(searchIconRef.current, 'header_bar', 'search_icon');
    }
  }, [isMobile]);

  return (
    <>
      <div className="bg-sk-red-500 from-sk-red-500 to-sk-red-900 flex justify-center bg-linear-to-r">
        <div className="w-full max-w-[940px] p-2 max-md:grid max-md:grid-cols-[1fr_3fr_1fr] md:flex md:items-center md:justify-center">
          {isMobile ? (
            <Sheet>
              {!menuOpen && (
                <SheetTrigger>
                  <HeaderIconLink iconName="menu" href="#" />
                </SheetTrigger>
              )}
              {menuOpen && (
                <SheetClose>
                  <HeaderIconLink iconName="close" href="#" />
                </SheetClose>
              )}
              <SheetContent
                side="left"
                className="text-sk-grey-0 bg-sk-blue-500 border-sk-blue-500 mt-12 h-[calc(100%-3rem)]"
                classNameOverlay="mt-12 h-[calc(100%-3rem)]"
                onOpenAutoFocus={() => setMenuOpen(true)}
                onCloseAutoFocus={() => setMenuOpen(false)}
                showIconClose={false}
              >
                <div>
                  <MenuGroup className="mx-4 mt-4">
                    <MenuItem href="https://www.speisekarte.de/gastro">
                      Infos für Gastronomen
                    </MenuItem>
                    <MenuItem href="https://www.speisekarte.de/restaurant_vorschlagen">
                      Restaurant vorschlagen
                    </MenuItem>
                    <MenuItem href="https://www.speisekarte.de/faq">
                      FAQ - Häufig gestellte Fragen
                    </MenuItem>
                    <MenuItem href="https://www.speisekarte.de/presse">Presse</MenuItem>
                    <MenuItem onClick={() => prgLink('https://www.speisekarte.de/bild-des-monats')}>
                      Bilder-Gewinnspiel
                    </MenuItem>
                    <MenuItem onClick={() => prgLink('https://www.speisekarte.de/about_us')}>
                      Über uns
                    </MenuItem>
                  </MenuGroup>
                  <MenuDivider />
                </div>
                <SheetFooter className="text-center text-sm">
                  <p>Oder folge uns auf</p>
                  <div className="flex justify-center">
                    <FacebookLogo variant="white" />
                    <InstagramLogo variant="white" />
                  </div>
                </SheetFooter>
              </SheetContent>
            </Sheet>
          ) : (
            <div className="md:hidden" />
          )}
          <a
            ref={logoRef}
            href="https://www.speisekarte.de/"
            className="mr-auto block max-md:mr-auto max-md:ml-auto"
            onClick={() => UBTracking.trackEvent.click('header_bar', 'logo')}
          >
            <BrandSymbolWithText className="h-8" />
          </a>
          <div className="flex items-center justify-end gap-4">
            {!isMobile && (
              <>
                <HeaderLink href="https://www.speisekarte.de/gastro">
                  Infos für Gastronome
                </HeaderLink>
                <HeaderLink href="https://www.speisekarte.de/registrieren">Registrieren</HeaderLink>
                <HeaderButton
                  ref={loginButtonRef}
                  href="https://www.speisekarte.de/anmelden"
                  onClick={() => UBTracking.trackEvent.click('header_bar', 'login', 'button')}
                >
                  Login
                </HeaderButton>
              </>
            )}

            {showSearchIcon && (
              <TextIconButton
                ref={searchIconRef}
                iconName="search"
                className="text-sk-grey-0!"
                onClick={() => {
                  !showSearch && window.scrollTo({ top: 0, behavior: 'smooth' });
                  toggleSearch();
                  UBTracking.trackEvent.click('header_bar', 'search_icon');
                }}
              />
            )}

            {isMobile && (
              <HeaderIconLink
                ref={loginLinkRef}
                href="https://www.speisekarte.de/anmelden"
                onClick={() => UBTracking.trackEvent.click('header_bar', 'login', 'icon')}
                iconName="profile"
              />
            )}
          </div>
        </div>
      </div>
    </>
  );
};

type HeaderLinkProps = React.PropsWithChildren<{
  href?: string;
}>;

const HeaderLink: React.FunctionComponent<HeaderLinkProps> = props => {
  return (
    <a href={props.href} className="text-sk-grey-0 text-sm">
      {props.children}
    </a>
  );
};

// type HeaderIconLinkProps = React.PropsWithChildren;
type HeaderIconLinkProps = {
  ref?: React.Ref<HTMLAnchorElement>;
  href: string;
  iconName: string;
  onClick?: () => void;
};

const HeaderIconLink: React.FunctionComponent<HeaderIconLinkProps> = props => {
  return (
    <a
      ref={props.ref}
      href={props.href}
      className="text-sk-grey-0 flex items-center"
      onClick={props.onClick}
    >
      <Icon name={props.iconName} className="text-2xl" />
    </a>
  );
};

type HeaderButtonProps = React.PropsWithChildren & {
  ref?: React.Ref<HTMLButtonElement | HTMLAnchorElement>;
  href: string;
  onClick?: () => void;
};

const HeaderButton: React.FunctionComponent<HeaderButtonProps> = props => {
  return (
    <Button ref={props.ref} variant="primary-contrast" href={props.href} onClick={props.onClick}>
      {props.children}
    </Button>
  );
};

type MenuGroupProps = React.PropsWithChildren & {
  className?: string;
};

const MenuGroup: React.FunctionComponent<MenuGroupProps> = props => {
  return (
    <div className={classList(props.className)}>
      {React.Children.map(props.children, (child, index) => (
        <>
          {child}
          {index < React.Children.count(props.children) - 1 && <MenuDivider />}
        </>
      ))}
    </div>
  );
};

type MenuItemProps = React.PropsWithChildren & {
  href?: string;
  onClick?: () => void;
};

const MenuItem: React.FunctionComponent<MenuItemProps> = props => {
  return (
    <a href={props.href} className="flex flex-row justify-between py-4" onClick={props.onClick}>
      <div>{props.children}</div>
      <Icon name="arrow-right" className="text-2xl" />
    </a>
  );
};

const MenuDivider: React.FunctionComponent = () => {
  return <hr className="border-sk-grey-900" />;
};
